import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';

interface Client {
    id: number;
    name: string;
}

interface ClientsResponse {
    data: Client[];
    meta: {
        total: number;
    };
}

/**
 * Hook para obtener la lista de clientes para usar en dropdowns/selects
 * Usa el mismo endpoint que el módulo de Clients (/teams-child)
 * Devuelve solo los clientes activos ordenados alfabéticamente
 */
export const useClientsDropdown = () => {
    const httpClient = createHttpClient(apiClientWH);

    const { data, isLoading, error } = useQuery<Client[], Error>({
        queryKey: ['clients-dropdown'],
        queryFn: async () => {
            const response = await httpClient.get<ClientsResponse>('/teams-child', {
                per_page: 1000, // Obtener un número grande para asegurar todos los clientes
                page: 1,
            });

            // Mapear solo los datos necesarios
            const clients: Client[] = response.data.map((client: any) => ({
                id: client.id,
                name: client.name,
            }));

            // Ordenar alfabéticamente
            clients.sort((a, b) => a.name.localeCompare(b.name));

            return clients;
        },
        staleTime: 5 * 60 * 1000, // 5 minutos - los clientes no cambian frecuentemente
        gcTime: 10 * 60 * 1000, // 10 minutos en cache
    });

    return {
        clients: data || [],
        total: data?.length || 0,
        isLoading,
        error: error?.message || null,
    };
};
